import { type DocumentNode, gql } from '@apollo/client';
import { jsonToGraphQLQuery, VariableType } from 'json-to-graphql-query';
import type { FieldValue } from './types';

interface DownloadLinksFields {
  globalId: FieldValue;
  itemId: FieldValue;
  downloadPath: FieldValue;
  filename: FieldValue;
}

interface DownloadLinksQueryResult {
  query: {
    __variables: {
      globalIds: '[String]';
      formatType: 'String';
      fallback: 'DownloadFallbackEnum';
    };
    downloadLinks: DownloadLinksFields & {
      __args: {
        globalIds: VariableType;
        formatType: VariableType;
        fallback: VariableType;
      };
    };
  };
}

interface SharedDownloadLinksQueryResult {
  query: {
    __variables: {
      globalIds: '[String]';
      formatType: 'String';
      fallback: 'DownloadFallbackEnum';
    };
    sharedDownloadLinks: DownloadLinksFields & {
      __args: {
        globalIds: VariableType;
        formatType: VariableType;
        fallback: VariableType;
      };
    };
  };
}

interface DownloadAvailabilityResultFields {
  formatType: FieldValue;
  formatLabel: FieldValue;
  available: FieldValue;
  total: FieldValue;
}

interface DownloadAvailabilityQueryResult {
  query: {
    __variables: {
      globalIds: '[String!]!';
    };
    downloadAvailability: DownloadAvailabilityResultFields & {
      __args: {
        globalIds: VariableType;
      };
    };
  };
}

interface SharedDownloadAvailabilityQueryResult {
  query: {
    __variables: {
      globalIds: '[String!]!';
    };
    sharedDownloadAvailability: DownloadAvailabilityResultFields & {
      __args: {
        globalIds: VariableType;
      };
    };
  };
}

interface SharedCollectionDownloadLinksQueryResult {
  query: {
    __variables: {
      collectionId: 'ID!';
      formatType: 'String!';
      fallback: 'DownloadFallbackEnum';
    };
    sharedCollectionDownloadLinks: DownloadLinksFields & {
      __args: {
        collectionId: VariableType;
        formatType: VariableType;
        fallback: VariableType;
      };
    };
  };
}

export const downloadLinks = (truify: boolean = false): DownloadLinksQueryResult => ({
  query: {
    __variables: {
      globalIds: '[String]',
      formatType: 'String',
      fallback: 'DownloadFallbackEnum',
    },
    downloadLinks: {
      __args: {
        globalIds: new VariableType('globalIds'),
        formatType: new VariableType('formatType'),
        fallback: new VariableType('fallback'),
      },
      globalId: truify,
      itemId: truify,
      downloadPath: truify,
      filename: truify,
    },
  },
});

export const buildDownloadLinksQ = (): DocumentNode => {
  const query = downloadLinks(true);
  const gQuery = jsonToGraphQLQuery(query, { pretty: true });
  return gql(gQuery);
};

export const buildSharedDownloadLinksQ = (): DocumentNode => {
  const query = downloadLinks(true);
  const modifiedQuery: SharedDownloadLinksQueryResult = {
    query: {
      __variables: query.query.__variables,
      sharedDownloadLinks: {
        ...query.query.downloadLinks,
      },
    },
  };
  const gQuery = jsonToGraphQLQuery(modifiedQuery, { pretty: true });
  return gql(gQuery);
};

export const downloadAvailability = (truify: boolean = false): DownloadAvailabilityQueryResult => ({
  query: {
    __variables: {
      globalIds: '[String!]!',
    },
    downloadAvailability: {
      __args: {
        globalIds: new VariableType('globalIds'),
      },
      formatType: truify,
      formatLabel: truify,
      available: truify,
      total: truify,
    },
  },
});

export const buildDownloadAvailabilityQ = (): DocumentNode => {
  const query = downloadAvailability(true);
  const gQuery = jsonToGraphQLQuery(query, { pretty: true });
  return gql(gQuery);
};

export const sharedDownloadAvailability = (truify: boolean = false): SharedDownloadAvailabilityQueryResult => {
  const query = downloadAvailability(truify);
  return {
    query: {
      __variables: query.query.__variables,
      sharedDownloadAvailability: {
        ...query.query.downloadAvailability,
      },
    },
  };
};

export const buildSharedDownloadAvailabilityQ = (): DocumentNode => {
  const query = sharedDownloadAvailability(true);
  const gQuery = jsonToGraphQLQuery(query, { pretty: true });
  return gql(gQuery);
};

export const sharedCollectionDownloadLinks = (truify: boolean = false): SharedCollectionDownloadLinksQueryResult => ({
  query: {
    __variables: {
      collectionId: 'ID!',
      formatType: 'String!',
      fallback: 'DownloadFallbackEnum',
    },
    sharedCollectionDownloadLinks: {
      __args: {
        collectionId: new VariableType('collectionId'),
        formatType: new VariableType('formatType'),
        fallback: new VariableType('fallback'),
      },
      globalId: truify,
      itemId: truify,
      downloadPath: truify,
      filename: truify,
    },
  },
});

export const buildSharedCollectionDownloadLinksQ = (): DocumentNode => {
  const query = sharedCollectionDownloadLinks(true);
  const gQuery = jsonToGraphQLQuery(query, { pretty: true });
  return gql(gQuery);
};
